import { Link, useLocation } from 'react-router-dom';
import { Container, Section, SocialWall } from '@components';
import useAuthStore from '@/stores/authStore';
import usePageMeta from '@/utils/usePageMeta';
import { useState, useEffect } from 'react';
import { doc, onSnapshot, collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db, trackEvent } from '../services/firebase';

// Link-styled CTAs (no <button> inside <a> — see docs/UIUX_AUDIT_2026-07-08.md H2)
const CTA_PRIMARY_CLASS =
  'inline-flex items-center justify-center rounded-lg px-8 py-4 text-lg font-medium transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-teal bg-primary-teal hover:bg-teal-600 text-white shadow-lg w-full sm:w-auto';
const CTA_OUTLINE_CLASS =
  'inline-flex items-center justify-center rounded-lg px-8 py-4 text-lg font-medium transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-300 border-2 border-sky-400/50 text-white hover:bg-white/10 hover:border-sky-300 w-full sm:w-auto';

// Every number here is verifiable against the resume / live systems — no
// invented agency stats.
const STATS = [
  { value: '2×', label: 'Databricks Certified — Associate + Professional' },
  { value: '13+', label: 'Years Working With Data' },
  { value: '7+', label: 'Years Building Data Pipelines' },
  { value: 'MSc', label: 'Engineering Physics' },
];

const SERVICES = [
  {
    title: 'Data Engineering',
    description:
      'Lakehouse pipelines on Databricks — Delta Live Tables, medallion architecture, CDC with Delta Change Data Feed, and Unity Catalog governance, in production.',
    icon: (
      <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2 3.58 3 8 3s8-1 8-3V7M4 7c0 2 3.58 3 8 3s8-1 8-3M4 7c0-2 3.58-3 8-3s8 1 8 3m-16 5c0 2 3.58 3 8 3s8-1 8-3" />
      </svg>
    ),
    iconBg: 'bg-primary-teal',
  },
  {
    title: 'AI & LLM Systems',
    description:
      'RAG pipelines, vector search, and LLM integrations (Gemini, OpenAI) that run unattended in production — like the content engine publishing this site.',
    icon: (
      <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    ),
    iconBg: 'bg-slate-700',
  },
  {
    title: 'Cloud Automation',
    description:
      'ETL/ELT and workflow automation on Google Cloud — Cloud Run, Cloud Functions, Firebase, Apache Beam / Dataflow — replacing manual work with scheduled, tested jobs.',
    icon: (
      <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.5 5.5 0 10-10.4 2.9A4 4 0 003 15z" />
      </svg>
    ),
    iconBg: 'bg-primary-teal',
  },
  {
    title: 'Analytics & Reporting',
    description:
      'Dashboards and social/content analytics with scheduled snapshots and automated reports — measurement that feeds the next decision, not a shelf.',
    icon: (
      <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
      </svg>
    ),
    iconBg: 'bg-primary-teal',
  },
];

// Real, shipped work — each entry maps to an actual repo or production system.
// Cards expand on click to show `summary`; `link` (when present) points at the
// live system.
const PROJECTS = [
  {
    title: 'This Site’s Content Loop Engine',
    status: 'Live in production',
    description:
      'A closed-loop content system: Gemini ideation → drafted posts → human-approved publishing to three X accounts → hourly analytics snapshots → weekly learning insights that rewrite next week’s prompts. The blog and news feed you see here are its output.',
    summary:
      'Runs unattended on Firebase Cloud Functions: an ideation step reads the latest AI news plus the previous week’s learning insights, drafts blog posts and X threads with Gemini, and queues them for one-click human approval. Published posts get analytics snapshots at 1, 24, and 168 hours, and a weekly job distills those numbers into citation-verified insights that rewrite the next ideation prompt. Every post traces back to its idea, prompt version, and the insight that motivated it.',
    link: { href: '/blogs', label: 'Read its output on the blog →', internal: true },
    tech: ['Firebase Functions', 'Firestore', 'Gemini', 'X API'],
  },
  {
    title: 'Databricks Lakehouse Pipelines',
    status: 'Data Engineer II · 2022–present',
    description:
      'Production Delta Live Tables pipelines on the medallion architecture, real-time incremental processing with Change Data Capture, Unity Catalog data governance, and cluster tuning that cut infrastructure cost.',
    summary:
      'Day-job work as a Data Engineer II: Delta Live Tables pipelines through Bronze/Silver/Gold medallion layers, Change Data Capture with Delta Change Data Feed for real-time incremental processing, Unity Catalog for fine-grained governance, and cluster/table tuning that cut infrastructure cost. These are internal employer systems, so there’s no public link — I’m happy to walk through the architecture on a call.',
    tech: ['Databricks', 'PySpark', 'Delta Lake', 'SQL'],
  },
  {
    title: 'SayMongol',
    status: 'Live at emazaalai.com',
    description:
      'A Mongolian language-learning app with skill paths, vocabulary practice, streaks, and paid subscriptions — auth, data, and billing wired end to end.',
    summary:
      'A full language-learning product built in Flutter and shipped to the web: structured skill paths, vocabulary practice, streaks and leaderboards, Google sign-in, and Stripe-powered subscriptions — all running on Firebase Auth, Firestore, and Cloud Functions.',
    link: { href: 'https://emazaalai.com/#/home', label: 'Try it live ↗' },
    tech: ['Flutter', 'Firebase', 'Stripe'],
  },
  {
    title: 'X/Twitter Analytics Suite',
    status: 'Feeds this site’s loop',
    description:
      'Python reporting across three X accounts producing automated performance reports — since formalized into the scheduled measurement step of the content loop above.',
    summary:
      'Python scripts that pull metrics for three X accounts — sentiment, most-active followers, top posts — and compile scheduled performance reports. Since formalized into the content loop’s hourly snapshot collector and weekly insight synthesis, so measurement feeds the next week’s content decisions automatically.',
    tech: ['Python', 'X API', 'Apps Script'],
  },
];

function ProjectCard({ project, expanded, onToggle }) {
  const p = project;
  return (
    <article
      className={`rounded-2xl border p-6 md:p-8 flex flex-col transition-colors ${
        expanded ? 'border-primary-teal/60 bg-white/20' : 'border-white/20 bg-white/15 hover:border-primary-teal/50'
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex items-start justify-between gap-4 mb-3 text-left w-full group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-teal rounded-lg"
      >
        <h3 className="text-xl font-bold text-white group-hover:text-primary-teal transition-colors">
          {p.title}
        </h3>
        <span className="flex items-center gap-2 shrink-0">
          <span className="rounded-full border border-primary-teal/40 bg-primary-teal/10 px-3 py-1 text-[11px] font-semibold tracking-wide text-primary-teal">
            {p.status}
          </span>
          <svg
            className={`w-4 h-4 text-primary-teal transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>
      <p className="text-sm text-slate-300 leading-relaxed flex-1 mb-5">{p.description}</p>
      {expanded && (
        <div className="mb-5 rounded-xl border border-primary-teal/30 bg-primary-navy/40 p-4 md:p-5">
          <p className="text-sm text-slate-200 leading-relaxed">{p.summary}</p>
          {p.link &&
            (p.link.internal ? (
              <Link
                to={p.link.href}
                className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary-teal hover:text-teal-300"
              >
                {p.link.label}
              </Link>
            ) : (
              <a
                href={p.link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary-teal hover:text-teal-300"
              >
                {p.link.label}
              </a>
            ))}
        </div>
      )}
      <ul className="flex flex-wrap gap-2">
        {p.tech.map((t) => (
          <li
            key={t}
            className="rounded-md border border-white/20 bg-white/10 px-2.5 py-1 text-xs text-slate-200"
          >
            {t}
          </li>
        ))}
      </ul>
    </article>
  );
}

function HeroSection({ hero, user }) {
  const h = hero || {};
  const badge = h.badge ?? 'DATA ENGINEER · WEEKENDS OPEN';
  const headline = h.headline ?? 'Building My Future, One Weekend at a Time';
  const subtitle =
    h.subtitle ??
    'I’m Byamba Enkhbat — a twice Databricks-certified data engineer in Chicago. Weekdays I build Lakehouse pipelines at my day job; weekends I build for myself and for clients. Everything on this site is generated, published, and measured by an automation loop I built on those weekends — and I can build that kind of system for you.';
  const ctaPrimaryLabel = h.ctaPrimaryLabel ?? 'Hire me for a weekend';
  const ctaPrimaryLink = h.ctaPrimaryLink ?? '/work-with-me';
  const ctaSecondaryLabel = h.ctaSecondaryLabel ?? 'See weekend builds';
  const ctaSecondaryLink = h.ctaSecondaryLink ?? '/#projects';
  const secondaryHref =
    ctaSecondaryLink.startsWith('/#')
      ? ctaSecondaryLink
      : ctaSecondaryLink.startsWith('#')
        ? `/${ctaSecondaryLink}`
        : ctaSecondaryLink;
  const useNativeAnchor =
    secondaryHref.includes('#') ||
    /^https?:\/\//i.test(secondaryHref) ||
    secondaryHref.startsWith('mailto:');

  return (
    <Section className="relative overflow-hidden bg-primary-navy text-white pt-12 pb-16 md:pt-16 md:pb-20 lg:pt-20 lg:pb-24">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage: `repeating-linear-gradient(-45deg, transparent, transparent 40px, rgba(20, 184, 166, 0.4) 40px, rgba(20, 184, 166, 0.4) 41px)`,
        }}
        aria-hidden
      />
      <Container className="relative z-10">
        <div className="grid gap-12 lg:gap-16 lg:grid-cols-2 lg:items-center">
          <div>
            {badge && (
              <div className="inline-flex items-center gap-2 rounded-full border border-primary-teal/40 bg-primary-teal/10 px-4 py-2 mb-6">
                <span className="h-1.5 w-1.5 rounded-full bg-primary-teal" aria-hidden />
                <span className="text-xs font-semibold tracking-wide text-primary-teal">{badge}</span>
              </div>
            )}
            <h1 className="text-4xl sm:text-5xl lg:text-[3.25rem] font-bold text-white leading-tight tracking-tight mb-6">
              {headline}
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-8 max-w-xl">
              {subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              {user ? (
                <Link to="/projects" className={CTA_PRIMARY_CLASS}>
                  Go to Dashboard
                </Link>
              ) : (
                <>
                  {ctaPrimaryLabel && (
                    <Link to={ctaPrimaryLink} className={CTA_PRIMARY_CLASS}>
                      {ctaPrimaryLabel}
                    </Link>
                  )}
                  {ctaSecondaryLabel && (
                    useNativeAnchor ? (
                      <a href={secondaryHref} className={CTA_OUTLINE_CLASS}>
                        {ctaSecondaryLabel}
                      </a>
                    ) : (
                      <Link to={secondaryHref} className={CTA_OUTLINE_CLASS}>
                        {ctaSecondaryLabel}
                      </Link>
                    )
                  )}
                </>
              )}
            </div>
          </div>
          {/* The real system behind this page, not a stock photo */}
          <div className="relative">
            <div
              className="rounded-2xl overflow-hidden ring-1 ring-white/25 shadow-2xl shadow-primary-teal/20 bg-[#0B1220]"
              role="img"
              aria-label="Diagram of the content loop pipeline that runs this site: ideation, production, review, publish, measure, learn"
            >
              <div className="flex items-center gap-1.5 px-4 py-3 border-b border-white/10 bg-white/5">
                <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" aria-hidden />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" aria-hidden />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" aria-hidden />
                <span className="ml-3 text-xs font-mono text-slate-400">content-loop — production</span>
              </div>
              <div className="p-5 md:p-6 font-mono text-[13px] leading-7 text-slate-300">
                <p><span className="text-primary-teal">▸ ideation</span> &nbsp;reads AI news + last week&rsquo;s insights</p>
                <p><span className="text-primary-teal">▸ production</span> &nbsp;drafts blog posts &amp; X threads (Gemini)</p>
                <p><span className="text-primary-teal">▸ review</span> &nbsp;a human approves before anything ships</p>
                <p><span className="text-primary-teal">▸ publish</span> &nbsp;blog + X connectors, exactly-once</p>
                <p><span className="text-primary-teal">▸ measure</span> &nbsp;snapshots at 1h / 24h / 7d</p>
                <p><span className="text-primary-teal">▸ learn</span> &nbsp;weekly insights rewrite the next prompt</p>
                <p className="mt-3 text-emerald-400">$ this page is the output — not a mockup<span className="animate-pulse">▌</span></p>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </Section>
  );
}

const Home = () => {
  usePageMeta({
    title: 'Byamba Enkhbat — Data Engineer. Building My Future, One Weekend at a Time.',
    description:
      'Twice Databricks-certified data engineer in Chicago, available for weekend projects: production data pipelines, RAG/LLM systems, and cloud automation on Google Cloud. This site runs on its own AI content loop.',
  });

  const { user } = useAuthStore();
  const location = useLocation();
  const [hero, setHero] = useState(null);
  const [news, setNews] = useState([]);
  const [expandedProject, setExpandedProject] = useState(null);

  // Scroll to /#section deep links (footer, hero CTA, external links) —
  // React Router doesn't handle hashes; retry briefly since data-driven
  // sections can mount after first paint (UIUX audit M4).
  useEffect(() => {
    if (!location.hash) return undefined;
    const id = location.hash.slice(1);
    let attempts = 0;
    let timer;
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      } else if (attempts < 10) {
        attempts += 1;
        timer = setTimeout(tryScroll, 100);
      }
    };
    tryScroll();
    return () => clearTimeout(timer);
  }, [location.hash]);

  const toggleProject = (title) => {
    const next = expandedProject === title ? null : title;
    if (next) {
      trackEvent('select_content', { content_type: 'project', item_id: title });
    }
    setExpandedProject(next);
  };

  useEffect(() => {
    const unsubHero = onSnapshot(
      doc(db, 'site_settings', 'hero'),
      snap => {
        if (snap.exists()) setHero(snap.data());
      },
      console.error,
    );
    return () => unsubHero();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const q = query(
          collection(db, 'ai_news'),
          orderBy('pubDate', 'desc'),
          limit(4),
        );
        const snap = await getDocs(q);
        if (cancelled) return;
        setNews(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Failed to load AI news:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="bg-primary-navy text-white min-h-screen">
      <HeroSection hero={hero} user={user} />

      {/* Stats */}
      <section id="stats" className="border-y border-white/20 bg-[#1E3A8A]/80">
        <Container className="py-10 md:py-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-0">
            {STATS.map((s, i) => (
              <div
                key={s.label}
                className={`text-center ${i > 0 ? 'lg:border-l lg:border-white/10 lg:pl-8' : ''}`}
              >
                <p className="text-3xl md:text-4xl font-bold text-primary-teal mb-1">{s.value}</p>
                <p className="text-sm text-slate-300">{s.label}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Solutions — the actual stack, not a generic checklist */}
      <Section id="solutions" className="bg-primary-navy py-16 md:py-20">
        <Container>
          <p className="text-center text-xs font-semibold tracking-widest text-primary-teal mb-3">WHAT I WORK WITH</p>
          <h2 className="text-center text-3xl md:text-4xl font-bold text-white mb-4">
            One engineer, a production-grade stack
          </h2>
          <p className="text-center text-slate-300 max-w-2xl mx-auto mb-10">
            No hand-offs, no account managers. You work directly with the person who designs,
            builds, and operates the system.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {['Databricks · Spark · Delta Lake', 'RAG & LLM pipelines', 'GCP · Firebase · Cloud Run', 'Python · SQL · PySpark'].map((item) => (
              <div
                key={item}
                className="flex items-center gap-3 rounded-xl border border-white/20 bg-white/15 px-4 py-3 text-white text-sm"
              >
                <svg className="w-5 h-5 shrink-0 text-primary-teal" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {item}
              </div>
            ))}
          </div>
        </Container>
      </Section>

      {/* The weekend model — how hiring a weekend actually works */}
      <Section id="weekends" className="bg-primary-navy py-16 md:py-20 border-t border-white/15">
        <Container>
          <p className="text-center text-xs font-semibold tracking-widest text-primary-teal mb-3">THE WEEKEND MODEL</p>
          <h2 className="text-center text-3xl md:text-4xl font-bold text-white mb-4">
            How hiring me for a weekend works
          </h2>
          <p className="text-center text-slate-300 max-w-2xl mx-auto mb-10">
            I have a day job I&rsquo;m good at, so I don&rsquo;t need your project — I choose it.
            That means honest scoping, no padding, and work I&rsquo;m proud to sign.
          </p>
          <div className="grid sm:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              {
                step: '1',
                title: 'Scope a weekend-sized slice',
                body: 'You and I talk during the week and cut your project into a piece that genuinely fits in a weekend — a working pipeline, an automation, a dashboard. If it doesn’t fit, I’ll say so.',
              },
              {
                step: '2',
                title: 'I build Saturday–Sunday',
                body: 'Heads-down build with the same standards as my day job: version-controlled, tested, deployed to your cloud — not a demo on my laptop.',
              },
              {
                step: '3',
                title: 'Monday you get something running',
                body: 'Every weekend ends with a runnable increment and a short write-up. Like what you see? Book the next weekend. Done? Walk away — no retainers.',
              },
            ].map((s) => (
              <div key={s.step} className="rounded-2xl border border-white/20 bg-white/15 p-6">
                <div className="w-10 h-10 rounded-xl bg-primary-teal flex items-center justify-center mb-4 text-lg font-bold text-white shadow-lg">
                  {s.step}
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{s.title}</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Services */}
      <Section id="services" className="bg-[#1B2F6B]/80 py-16 md:py-20 border-t border-white/15">
        <Container>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10 md:mb-12">
            <div>
              <p className="text-xs font-semibold tracking-widest text-primary-teal mb-2">WHAT I DO ON WEEKENDS</p>
              <h2 className="text-3xl md:text-4xl font-bold text-white">Services, grounded in shipped work</h2>
              <p className="text-slate-300 mt-2 max-w-2xl">
                My weekends go into building — for myself, and for one client at a time. Projects are
                scoped into weekend-sized slices, so you see something running every Monday.
              </p>
            </div>
            <Link to="/work-with-me" className="text-sm font-medium text-primary-teal hover:text-teal-300 transition-colors shrink-0">
              Start a conversation →
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES.map((svc) => (
              <article
                key={svc.title}
                className="rounded-2xl border border-white/20 bg-white/15 p-6 flex flex-col hover:border-primary-teal/50 transition-colors"
              >
                <div className={`w-12 h-12 rounded-xl ${svc.iconBg} flex items-center justify-center mb-4 shadow-lg`}>
                  {svc.icon}
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{svc.title}</h3>
                <p className="text-sm text-slate-300 leading-relaxed flex-1 mb-4">{svc.description}</p>
                <Link to="/work-with-me" className="inline-flex items-center gap-1 text-sm font-semibold text-primary-teal hover:text-teal-300">
                  Work with me
                  <span aria-hidden>→</span>
                </Link>
              </article>
            ))}
          </div>
        </Container>
      </Section>

      {/* Selected work — real projects, verifiable */}
      <Section id="projects" className="bg-primary-navy py-16 md:py-20 border-t border-white/15">
        <Container>
          <div className="mb-10 md:mb-12">
            <p className="text-xs font-semibold tracking-widest text-primary-teal mb-2">PROOF OF WORK</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white">Real systems, not stock photos</h2>
            <p className="text-slate-300 mt-2 max-w-2xl">
              Selected projects I built and operate. The first one is running this very page —
              click any card for the full story.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {PROJECTS.map((p) => (
              <ProjectCard
                key={p.title}
                project={p}
                expanded={expandedProject === p.title}
                onToggle={() => toggleProject(p.title)}
              />
            ))}
          </div>
        </Container>
      </Section>

      {/* Building in public — interactive wall of real posts shipped to X */}
      <SocialWall />

      {/* AI Automation News */}
      {news.length > 0 && (
        <Section id="ai-news" className="bg-[#1B2F6B]/80 py-16 md:py-20 border-t border-white/15">
          <Container>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10 md:mb-12">
              <div>
                <p className="text-xs font-semibold tracking-widest text-primary-teal mb-2">LATEST NEWS</p>
                <h2 className="text-3xl md:text-4xl font-bold text-white">AI Automation, today</h2>
                <p className="text-slate-300 mt-2 max-w-2xl">
                  Curated headlines on AI automation, ingested daily by this site’s own pipeline.
                </p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {news.map((item) => {
                const href = item.link || '#';
                // Guard: a non-string pubDate (Firestore Timestamp/number)
                // must not crash the render (UIUX audit M2).
                const pubDate =
                  typeof item.pubDate === 'string'
                    ? new Date(item.pubDate.replace(' ', 'T') + 'Z')
                    : item.pubDate?.toDate?.() ?? null;
                return (
                  <a
                    key={item.id}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group rounded-2xl border border-white/20 bg-white/15 overflow-hidden flex flex-col hover:border-primary-teal/50 hover:bg-white/20 transition-colors"
                  >
                    <div className="aspect-video w-full bg-slate-800 overflow-hidden">
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt={item.title || 'News image'}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                          decoding="async"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-500 text-xs">
                          No image
                        </div>
                      )}
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <div className="flex items-center gap-2 text-xs text-slate-400 mb-2">
                        {item.sourceName && (
                          <span className="font-semibold text-primary-teal truncate">
                            {item.sourceName}
                          </span>
                        )}
                        {pubDate && !isNaN(pubDate) && (
                          <>
                            <span aria-hidden>•</span>
                            <span>
                              {pubDate.toLocaleDateString(undefined, {
                                month: 'short',
                                day: 'numeric',
                              })}
                            </span>
                          </>
                        )}
                      </div>
                      <h3 className="text-base font-bold text-white mb-2 line-clamp-3 group-hover:text-primary-teal transition-colors">
                        {item.title || 'Untitled'}
                      </h3>
                      {item.description && (
                        <p className="text-sm text-slate-300 leading-relaxed line-clamp-3 mb-4">
                          {item.description}
                        </p>
                      )}
                      <span className="mt-auto inline-flex items-center gap-1 text-sm font-semibold text-primary-teal group-hover:text-teal-300">
                        Read more
                        <span aria-hidden>→</span>
                      </span>
                    </div>
                  </a>
                );
              })}
            </div>
          </Container>
        </Section>
      )}

      {/* About — the person, not a fictional agency */}
      <Section id="about" className="bg-primary-navy py-16 md:py-20 border-t border-white/15">
        <Container>
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-xs font-semibold tracking-widest text-primary-teal mb-3">WHO I AM</p>
            <img
              src="/byamba.jpg"
              alt="Portrait of Byamba Enkhbat"
              className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover mx-auto mb-6 ring-4 ring-primary-teal/40 shadow-xl shadow-primary-teal/20"
              loading="lazy"
            />
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">About me</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              I&rsquo;m <strong className="text-white">Byamba Enkhbat</strong> — a Chicago-based
              Data Engineer II holding both Databricks Data Engineer certifications (Associate and
              Professional), with an MSc in Engineering Physics. By day I build Delta Live Tables
              pipelines, CDC flows, and Unity Catalog governance on the Databricks Lakehouse.
              Before that I spent three years as a freelance cloud data engineer on Upwork,
              automating ETL for clients worldwide on Google Cloud.
            </p>
            <p className="text-slate-300 leading-relaxed mb-6">
              My weekends go into building my own future: this site, its AI content engine, a
              language-learning app, an exam-prep product. I build in public — the system that
              writes, publishes, and measures this site&rsquo;s blog and social posts is my own
              weekend production work, and it&rsquo;s the same quality of system I&rsquo;ll build for you.
            </p>
            {/* Verifiable proof: real credential + code links, not claims */}
            <div className="mb-10 flex flex-wrap justify-center gap-3">
              <a
                href="https://github.com/byambaa1982"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-sm font-medium text-white hover:border-primary-teal/60 hover:text-primary-teal transition-colors"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                </svg>
                See the code — github.com/byambaa1982
              </a>
              <a
                href="https://credentials.databricks.com/056070f5-1581-4e24-8996-82b1f1e23b20#acc.3C6pjHkb"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-sm font-medium text-white hover:border-primary-teal/60 hover:text-primary-teal transition-colors"
              >
                <svg className="w-4 h-4 text-primary-teal" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Verify: Data Engineer Associate
              </a>
              <a
                href="https://credentials.databricks.com/d9114e66-f556-4da1-a383-fc65cb512027#acc.8lQgipFn"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-sm font-medium text-white hover:border-primary-teal/60 hover:text-primary-teal transition-colors"
              >
                <svg className="w-4 h-4 text-primary-teal" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Verify: Data Engineer Professional
              </a>
            </div>
            <div className="rounded-2xl border border-white/20 bg-white/15 px-6 py-8 md:px-10">
              <p className="text-xs font-semibold tracking-widest text-primary-teal mb-4">HOW I WORK</p>
              <blockquote className="text-lg md:text-xl text-slate-200 italic leading-relaxed">
                &ldquo;Proof over promises: this site is its own case study. If I&rsquo;m selling you
                automation, you should be able to watch mine run.&rdquo;
              </blockquote>
            </div>
          </div>
        </Container>
      </Section>

      {/* Bottom CTA — route to the lead form, not account signup */}
      {!user && (
        <Section className="py-16 md:py-20 bg-[#1E3A8A]/80 border-t border-white/20">
          <Container>
            <div className="max-w-4xl mx-auto rounded-2xl border border-white/20 bg-white/15 px-8 py-12 md:px-12 text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                Have a pipeline to build or a process to automate?
              </h2>
              <p className="text-slate-300 mb-8">
                My next free weekend could be yours. Tell me what you&rsquo;re trying to do —
                I read every inquiry personally and I&rsquo;ll tell you honestly if it fits.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/work-with-me" className={CTA_PRIMARY_CLASS}>
                  Tell me about your project
                </Link>
                <a href="mailto:enkhbat@byamba.net" className={CTA_OUTLINE_CLASS}>
                  Email me
                </a>
              </div>
            </div>
          </Container>
        </Section>
      )}
    </div>
  );
};

export default Home;
